/**
 * LocaleConfigPanel
 *
 * UI for initializing and managing the canonical project locale configuration.
 * Catalog and custom locales are both stored explicitly so authored workflows
 * share the same fail-closed locale authority.
 *
 * @module locale-config/components
 */

import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Stack,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useLocaleConfig } from '../hooks/useLocaleConfig';
import { isKnownLocale, LOCALE_CATALOG } from '../constants/localeCatalog';
import type { ProjectLocale } from '../types/localeConfig.types';
import { LOCALE_CONFIG } from '../selectors';
import { LocaleAuthoringSection } from './LocaleAuthoringSection';

interface LocaleConfigPanelProps {
  projectId: string;
}

export const LocaleConfigPanel: React.FC<LocaleConfigPanelProps> = ({ projectId }) => {
  const {
    config,
    configured,
    loading,
    error,
    saving,
    addLocale,
    removeLocale,
    setDefaultLocale,
    resetToDefaults,
  } = useLocaleConfig(projectId);

  // Add Custom dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [customCode, setCustomCode] = useState('');
  const [customLabel, setCustomLabel] = useState('');
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [catalogDialogOpen, setCatalogDialogOpen] = useState(false);
  const [catalogCode, setCatalogCode] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);

  const availableCatalogLocales = LOCALE_CATALOG.filter((candidate) => (
    !config.locales.some((locale) => locale.code === candidate.code)
  ));

  const handleOpenDialog = useCallback(() => {
    setCustomCode('');
    setCustomLabel('');
    setDialogError(null);
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
  }, []);

  const handleAddCustom = useCallback(async () => {
    const code = customCode.trim();
    const label = customLabel.trim();

    if (!code) {
      setDialogError('Locale code is required');
      return;
    }
    if (!label) {
      setDialogError('Label is required');
      return;
    }

    // Check for duplicates (in catalog or already added)
    if (isKnownLocale(code)) {
      setDialogError(`"${code}" already exists in the standard catalog`);
      return;
    }
    if (config.locales.some(l => l.code === code)) {
      setDialogError(`"${code}" is already added`);
      return;
    }

    const locale: ProjectLocale = { code, label };
    try {
      await addLocale(locale);
      setDialogOpen(false);
    } catch {
      setDialogError('Failed to save locale');
    }
  }, [customCode, customLabel, config.locales, addLocale]);

  const handleRemove = useCallback((code: string) => {
    setActionError(null);
    removeLocale(code).catch((cause) => {
      setActionError(cause instanceof Error ? cause.message : 'Failed to remove project language.');
    });
  }, [removeLocale]);

  const handleInitialize = useCallback(() => {
    setActionError(null);
    resetToDefaults().catch((cause) => {
      setActionError(cause instanceof Error ? cause.message : 'Failed to save project language setup.');
    });
  }, [resetToDefaults]);

  const handleSetDefault = useCallback((code: string) => {
    setActionError(null);
    setDefaultLocale(code).catch((cause) => {
      setActionError(cause instanceof Error ? cause.message : 'Failed to change the default language.');
    });
  }, [setDefaultLocale]);

  const handleOpenCatalogDialog = useCallback(() => {
    setCatalogCode(availableCatalogLocales[0]?.code ?? '');
    setDialogError(null);
    setCatalogDialogOpen(true);
  }, [availableCatalogLocales]);

  const handleAddCatalog = useCallback(async () => {
    const locale = LOCALE_CATALOG.find((candidate) => candidate.code === catalogCode);
    if (!locale) {
      setDialogError('Choose a catalog language.');
      return;
    }
    try {
      await addLocale({ code: locale.code, label: locale.label });
      setCatalogDialogOpen(false);
    } catch {
      setDialogError('Failed to save project language.');
    }
  }, [addLocale, catalogCode]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">Failed to load locale config: {error.message}</Alert>;
  }

  return (
    <Box data-testid={LOCALE_CONFIG.panel}>
      {!configured && (
        <Alert
          severity="info"
          data-testid={LOCALE_CONFIG.setupRequired}
          sx={{ mb: 2 }}
          action={(
            <Button
              color="inherit"
              size="small"
              disabled={saving}
              onClick={handleInitialize}
              data-testid={LOCALE_CONFIG.initialize}
              sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
            >
              Use English (US)
            </Button>
          )}
        >
          Save at least one project language before creating localized funnels, onboarding flows, or creatives. You can use English (US) now or add another language below.
        </Alert>
      )}
      {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}

      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Project languages
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        These exact language codes are shared by funnels, onboarding flows, and Segmently-owned creatives. Choose a default language and add catalog or custom languages as needed.
      </Typography>

      {config.locales.length > 0 ? (
        <List dense sx={{ mb: 1 }}>
          {config.locales.map((locale) => (
            <ListItem
              key={locale.code}
              sx={{ py: 0.5 }}
              data-testid={LOCALE_CONFIG.languageRow(locale.code)}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip
                      label={locale.code}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                    />
                    <Typography variant="body2">{locale.label}</Typography>
                    {locale.code === config.defaultLocale && <Chip label="Default" size="small" color="primary" />}
                    {!isKnownLocale(locale.code) && <Chip label="Custom" size="small" />}
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {locale.code !== config.defaultLocale && (
                    <Button
                      size="small"
                      disabled={saving}
                      onClick={() => handleSetDefault(locale.code)}
                      data-testid={LOCALE_CONFIG.makeDefault(locale.code)}
                    >
                      Make default
                    </Button>
                  )}
                  <IconButton
                    size="small"
                    onClick={() => handleRemove(locale.code)}
                    disabled={saving || locale.code === config.defaultLocale}
                    aria-label={`Remove ${locale.label}`}
                    title={locale.code === config.defaultLocale ? 'Choose another default language before removing this one' : 'Remove project language'}
                    data-testid={LOCALE_CONFIG.remove(locale.code)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontStyle: 'italic' }}>
          No project languages configured yet.
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleOpenCatalogDialog}
          disabled={saving || availableCatalogLocales.length === 0}
          data-testid={LOCALE_CONFIG.catalogAdd}
        >
          Add catalog language
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
          disabled={saving}
        >
          Add custom language
        </Button>
        {saving && <CircularProgress size={20} />}
      </Box>

      <Dialog
        open={catalogDialogOpen}
        onClose={() => setCatalogDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        data-testid={LOCALE_CONFIG.catalogDialog}
      >
        <DialogTitle>Add catalog language</DialogTitle>
        <DialogContent>
          <TextField
            select
            autoFocus
            label="Catalog language"
            value={catalogCode}
            onChange={(event) => setCatalogCode(event.target.value)}
            fullWidth
            sx={{ mt: 1 }}
          >
            {availableCatalogLocales.map((locale) => (
              <MenuItem key={locale.code} value={locale.code}>
                {locale.label} · {locale.code}
              </MenuItem>
            ))}
          </TextField>
          {dialogError && <Alert severity="error" sx={{ mt: 2 }}>{dialogError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatalogDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => void handleAddCatalog()} disabled={!catalogCode || saving}>Add language</Button>
        </DialogActions>
      </Dialog>

      {/* Add Custom Locale Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Add custom language</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {dialogError && (
              <Alert severity="error" onClose={() => setDialogError(null)}>
                {dialogError}
              </Alert>
            )}
            <TextField
              autoFocus
              label="Locale Code"
              placeholder="e.g. es-419, my-custom"
              value={customCode}
              onChange={(e) => setCustomCode(e.target.value)}
              fullWidth
              required
              helperText="BCP 47 locale code"
            />
            <TextField
              label="Display Label"
              placeholder="e.g. Spanish (Latin America)"
              value={customLabel}
              onChange={(e) => setCustomLabel(e.target.value)}
              fullWidth
              required
              helperText="Human-readable name for this locale"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAddCustom}
            disabled={!customCode.trim() || !customLabel.trim() || saving}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {configured ? (
        <LocaleAuthoringSection projectId={projectId} />
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
          Localization profiles and glossaries become available after the project language setup is saved.
        </Typography>
      )}
    </Box>
  );
};
